import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Checkbox, Select, Switch, Tag } from "antd";
import { CloseIcon } from "../../../assets/icons";
import {
  Overlay,
  Modal,
  CloseButton,
  Header,
} from "../../../components/UploadButton/uploadButtonModals/SelectTemplateStyled";
import {
  createRoutingRule,
  deleteRoutingRule,
  fetchRoutingRuleOptions,
  fetchRoutingRules,
  updateRoutingRule,
} from "../../../service/settings";

const Panel = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  min-width: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  color: #111827;
`;

const Intro = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  max-width: 860px;
`;

const Banner = styled.div`
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12px;
  margin-bottom: 12px;
`;

const Button = styled.button`
  border: 1px solid ${(p) => (p.$danger ? "#fca5a5" : "#d1d5db")};
  background: ${(p) => (p.$primary ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : p.$danger ? "#dc2626" : "#374151")};
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Table = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 0.9fr) 90px minmax(200px, 1.5fr) minmax(170px, 1.1fr) 70px auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: 0;
  }
`;

const HeaderCell = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Cell = styled.div`
  min-width: 0;
  font-size: 13px;
  color: ${(p) => (p.$muted ? "#9ca3af" : "#111827")};
`;

const RuleName = styled.div`
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RuleDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
`;

const RuleModal = styled(Modal)`
  width: 560px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
`;

const ModalBody = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModalLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const FieldHint = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: -6px;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  &:focus {
    border-color: #0c53bb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid #f3f4f6;
`;

const ModalError = styled.div`
  color: #dc2626;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
`;

const EMPTY_FORM = {
  id: null,
  name: "",
  description: "",
  kind: "route",
  is_active: true,
  match_families: [],
  match_categories: [],
  require_no_case_manager: false,
  match_principal_attorney_sf_ids: [],
  match_case_manager_sf_ids: [],
  match_case_types: [],
  recipient_ids: [],
  notify_emails: [],
};

const fieldLabel = (field) =>
  String(field)
    .replace(/^_+|_+$/g, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const getApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    const lines = [];
    const flatten = (value, prefix = "") => {
      if (value == null) return;
      if (typeof value === "string") {
        lines.push(prefix ? `${prefix}: ${value}` : value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => flatten(item, prefix));
      } else if (typeof value === "object") {
        Object.entries(value).forEach(([key, nested]) =>
          flatten(nested, prefix ? `${prefix} ${fieldLabel(key)}` : fieldLabel(key))
        );
      } else {
        lines.push(prefix ? `${prefix}: ${String(value)}` : String(value));
      }
    };
    flatten(data);
    if (lines.length) return lines.join("\n");
  }
  const status = err?.response?.status;
  if (status === 401 || status === 403) {
    return "You do not have permission to perform this action.";
  }
  if (!err?.response) {
    return "Could not reach DMS. Please check your connection and try again.";
  }
  return fallback;
};

const toFormState = (rule) => ({
  id: rule.id,
  name: rule.name || "",
  description: rule.description || "",
  kind: rule.kind || "route",
  is_active: Boolean(rule.is_active),
  match_families: rule.match_families || [],
  match_categories: rule.match_categories || [],
  require_no_case_manager: Boolean(rule.require_no_case_manager),
  match_principal_attorney_sf_ids: rule.match_principal_attorney_sf_ids || [],
  match_case_manager_sf_ids: rule.match_case_manager_sf_ids || [],
  match_case_types: rule.match_case_types || [],
  recipient_ids: (rule.recipients || []).map((user) => user.id),
  notify_emails: rule.notify_emails || [],
});

const toPayload = (form) => {
  const suppress = form.kind === "suppress";
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    kind: form.kind,
    is_active: form.is_active,
    match_categories: form.match_categories,
    // Suppress rules are category-only; the backend rejects anything
    // else, so the ignored conditions are cleared rather than sent.
    match_families: suppress ? [] : form.match_families,
    require_no_case_manager: suppress ? false : form.require_no_case_manager,
    match_principal_attorney_sf_ids: suppress
      ? []
      : form.match_principal_attorney_sf_ids,
    match_case_manager_sf_ids: suppress ? [] : form.match_case_manager_sf_ids,
    match_case_types: suppress ? [] : form.match_case_types,
    recipient_ids: suppress ? [] : form.recipient_ids,
    notify_emails: suppress ? [] : form.notify_emails,
  };
};

export default function RoutingRules() {
  const [rules, setRules] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [modal, setModal] = useState(null); // { mode, form, error, saving }

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [rulesResponse, optionsResponse] = await Promise.all([
        fetchRoutingRules(),
        fetchRoutingRuleOptions(),
      ]);
      setRules(rulesResponse?.data?.rules || []);
      setOptions(optionsResponse?.data || null);
    } catch (err) {
      console.error("Failed to load routing rules", err);
      setLoadError(getApiErrorMessage(err, "Could not load routing rules."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const usersBySfId = useMemo(() => {
    const map = {};
    (options?.users || []).forEach((user) => {
      if (user.salesforce_id) map[user.salesforce_id] = user;
    });
    return map;
  }, [options]);

  const familyLabels = useMemo(() => {
    const map = {};
    (options?.families || []).forEach((f) => {
      map[f.value] = f.label;
    });
    return map;
  }, [options]);

  const userOptions = useMemo(
    () =>
      (options?.users || []).map((user) => ({
        value: user.id,
        label: user.name === user.email ? user.name : `${user.name} (${user.email})`,
      })),
    [options]
  );

  const sfUserOptions = useMemo(
    () =>
      (options?.users || [])
        .filter((user) => user.salesforce_id)
        .map((user) => ({ value: user.salesforce_id, label: user.name })),
    [options]
  );

  const describeConditions = (rule) => {
    const parts = [];
    if (rule.match_families?.length) {
      parts.push(
        rule.match_families.map((slug) => familyLabels[slug] || slug).join(", ")
      );
    }
    if (rule.match_categories?.length) {
      parts.push(`Category: ${rule.match_categories.join(", ")}`);
    }
    if (rule.require_no_case_manager) parts.push("No case manager");
    if (rule.match_principal_attorney_sf_ids?.length) {
      parts.push(
        `PA: ${rule.match_principal_attorney_sf_ids
          .map((sfId) => usersBySfId[sfId]?.name || sfId)
          .join(", ")}`
      );
    }
    if (rule.match_case_manager_sf_ids?.length) {
      parts.push(
        `CM: ${rule.match_case_manager_sf_ids
          .map((sfId) => usersBySfId[sfId]?.name || sfId)
          .join(", ")}`
      );
    }
    if (rule.match_case_types?.length) {
      parts.push(`Case type: ${rule.match_case_types.join(", ")}`);
    }
    return parts.length ? parts.join(" · ") : "Every document";
  };

  const describeRecipients = (rule) => {
    if (rule.kind === "suppress") return "—";
    const names = (rule.recipients || []).map((user) => user.name);
    const emails = rule.notify_emails || [];
    const all = [...names, ...emails.map((address) => `${address} (email)`)];
    return all.length ? all.join(", ") : "No recipients yet";
  };

  const openModal = (rule = null) => {
    setModal({
      mode: rule ? "edit" : "create",
      form: rule ? toFormState(rule) : { ...EMPTY_FORM },
      error: "",
      saving: false,
    });
  };

  const closeModal = () => setModal(null);

  const setForm = (patch) => {
    setModal((current) =>
      current ? { ...current, form: { ...current.form, ...patch }, error: "" } : current
    );
  };

  const submitModal = async (event) => {
    event.preventDefault();
    const { form, mode } = modal;
    const name = form.name.trim();
    if (!name) {
      setModal((current) => ({ ...current, error: "Rule name is required." }));
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
      setModal((current) => ({
        ...current,
        error:
          "Rule name can only contain letters, numbers, hyphens, and underscores (it is a stable identifier).",
      }));
      return;
    }
    setModal((current) => ({ ...current, saving: true, error: "" }));
    try {
      if (mode === "edit") {
        await updateRoutingRule(form.id, toPayload(form));
      } else {
        await createRoutingRule(toPayload(form));
      }
      await load();
      closeModal();
    } catch (err) {
      console.error("Failed to save routing rule", err);
      setModal((current) =>
        current
          ? {
              ...current,
              saving: false,
              error: getApiErrorMessage(err, "Could not save this rule."),
            }
          : current
      );
    }
  };

  const handleToggleActive = async (rule, isActive) => {
    setRules((current) =>
      current.map((row) => (row.id === rule.id ? { ...row, is_active: isActive } : row))
    );
    try {
      await updateRoutingRule(rule.id, { is_active: isActive });
    } catch (err) {
      console.error("Failed to toggle routing rule", err);
      setRules((current) =>
        current.map((row) =>
          row.id === rule.id ? { ...row, is_active: !isActive } : row
        )
      );
      setLoadError(getApiErrorMessage(err, "Could not update the rule."));
    }
  };

  const handleDelete = async (rule) => {
    if (
      !window.confirm(
        `Delete routing rule "${rule.name}"? Turning it off keeps it for later; deleting is permanent.`
      )
    ) {
      return;
    }
    try {
      await deleteRoutingRule(rule.id);
      await load();
    } catch (err) {
      console.error("Failed to delete routing rule", err);
      setLoadError(getApiErrorMessage(err, "Could not delete the rule."));
    }
  };

  const modalForm = modal?.form;
  const isSuppress = modalForm?.kind === "suppress";

  return (
    <Panel>
      <HeaderRow>
        <Title>Recipient Routing Rules</Title>
        <Button type="button" $primary onClick={() => openModal()}>
          New rule
        </Button>
      </HeaderRow>
      <Intro>
        Route mail-filed documents to people beyond the matter team. The principal
        attorney and case manager always see their matters&apos; documents — these rules
        add extra recipients (inbox + notification, or a group-email link) when a
        document matches, or keep a category out of the Attorney Inbox entirely.
      </Intro>
      {options && !options.routing_enabled && (
        <Banner>
          The routing engine is currently disabled, so these rules are saved but not
          applied. Ask an operator to enable DOCUMENT_ROUTING_RULES_ENABLED.
        </Banner>
      )}
      {loadError && <Banner role="alert">{loadError}</Banner>}
      {loading && rules.length === 0 && <Empty>Loading routing rules…</Empty>}
      {!loading && rules.length === 0 && !loadError && (
        <Empty>No routing rules yet. Create one to route documents to extra recipients.</Empty>
      )}
      {rules.length > 0 && (
        <Table>
          <Row>
            <HeaderCell>Rule</HeaderCell>
            <HeaderCell>Type</HeaderCell>
            <HeaderCell>When a document matches</HeaderCell>
            <HeaderCell>Goes to</HeaderCell>
            <HeaderCell>Active</HeaderCell>
            <HeaderCell />
          </Row>
          {rules.map((rule) => (
            <Row key={rule.id}>
              <Cell>
                <RuleName title={rule.name}>{rule.name}</RuleName>
                {rule.description && (
                  <RuleDescription title={rule.description}>
                    {rule.description}
                  </RuleDescription>
                )}
              </Cell>
              <Cell>
                {rule.kind === "suppress" ? (
                  <Tag color="volcano">Suppress</Tag>
                ) : (
                  <Tag color="blue">Route</Tag>
                )}
              </Cell>
              <Cell>{describeConditions(rule)}</Cell>
              <Cell $muted={rule.kind === "suppress"}>{describeRecipients(rule)}</Cell>
              <Cell>
                <Switch
                  size="small"
                  checked={rule.is_active}
                  onChange={(checked) => handleToggleActive(rule, checked)}
                  aria-label={`Toggle ${rule.name}`}
                />
              </Cell>
              <ActionGroup>
                <Button type="button" onClick={() => openModal(rule)}>
                  Edit
                </Button>
                <Button type="button" $danger onClick={() => handleDelete(rule)}>
                  Delete
                </Button>
              </ActionGroup>
            </Row>
          ))}
        </Table>
      )}

      {modal && (
        <Overlay>
          <RuleModal>
            <CloseButton onClick={closeModal} aria-label="Close rule modal">
              <CloseIcon />
            </CloseButton>
            <Header>{modal.mode === "edit" ? "Edit Routing Rule" : "New Routing Rule"}</Header>
            <ModalBody onSubmit={submitModal}>
              <ModalLabel htmlFor="routing-rule-name">Rule Name</ModalLabel>
              <ModalInput
                id="routing-rule-name"
                value={modalForm.name}
                onChange={(event) => setForm({ name: event.target.value })}
                placeholder="e.g. rating-decisions-to-rana"
                autoFocus
              />
              <ModalLabel htmlFor="routing-rule-description">Description</ModalLabel>
              <ModalInput
                id="routing-rule-description"
                value={modalForm.description}
                onChange={(event) => setForm({ description: event.target.value })}
                placeholder="What is this rule for?"
              />
              <ModalLabel id="routing-rule-kind-label">Rule Type</ModalLabel>
              <Select
                aria-labelledby="routing-rule-kind-label"
                value={modalForm.kind}
                onChange={(kind) => setForm({ kind })}
                options={(options?.kinds || []).map((kind) => ({
                  value: kind.value,
                  label: kind.label,
                }))}
              />

              <SectionTitle>When a document matches</SectionTitle>
              {!isSuppress && (
                <>
                  <ModalLabel id="routing-rule-families-label">Document Titles</ModalLabel>
                  <Select
                    aria-labelledby="routing-rule-families-label"
                    mode="multiple"
                    allowClear
                    placeholder="Any title"
                    value={modalForm.match_families}
                    onChange={(match_families) => setForm({ match_families })}
                    options={(options?.families || []).map((family) => ({
                      value: family.value,
                      label: family.label,
                    }))}
                  />
                </>
              )}
              <ModalLabel id="routing-rule-categories-label">Categories</ModalLabel>
              <Select
                aria-labelledby="routing-rule-categories-label"
                mode="multiple"
                allowClear
                placeholder={isSuppress ? "Pick the categories to suppress" : "Any category"}
                value={modalForm.match_categories}
                onChange={(match_categories) => setForm({ match_categories })}
                options={(options?.categories || []).map((category) => ({
                  value: category.value,
                  label: category.label,
                }))}
              />
              {!isSuppress && (
                <FieldHint>
                  A document matches if its title OR its category is listed. Leave both
                  empty to match every document.
                </FieldHint>
              )}
              {isSuppress && (
                <FieldHint>
                  Documents in these categories skip the Attorney Inbox entirely and file
                  straight into their matter.
                </FieldHint>
              )}

              {!isSuppress && (
                <>
                  <ModalLabel id="routing-rule-pa-label">
                    Only these Principal Attorneys
                  </ModalLabel>
                  <Select
                    aria-labelledby="routing-rule-pa-label"
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Any principal attorney"
                    value={modalForm.match_principal_attorney_sf_ids}
                    onChange={(match_principal_attorney_sf_ids) =>
                      setForm({ match_principal_attorney_sf_ids })
                    }
                    options={sfUserOptions}
                  />
                  <ModalLabel id="routing-rule-cm-label">Only these Case Managers</ModalLabel>
                  <Select
                    aria-labelledby="routing-rule-cm-label"
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Any case manager"
                    value={modalForm.match_case_manager_sf_ids}
                    onChange={(match_case_manager_sf_ids) =>
                      setForm({ match_case_manager_sf_ids })
                    }
                    options={sfUserOptions}
                  />
                  <ModalLabel id="routing-rule-case-types-label">Case Types</ModalLabel>
                  <Select
                    aria-labelledby="routing-rule-case-types-label"
                    mode="tags"
                    allowClear
                    placeholder='Any case type — type to add, e.g. "SSDI"'
                    value={modalForm.match_case_types}
                    onChange={(match_case_types) => setForm({ match_case_types })}
                    open={false}
                  />
                  <InlineRow>
                    <Checkbox
                      checked={modalForm.require_no_case_manager}
                      onChange={(event) =>
                        setForm({ require_no_case_manager: event.target.checked })
                      }
                    >
                      Only matters with no case manager
                    </Checkbox>
                  </InlineRow>

                  <SectionTitle>Send it to</SectionTitle>
                  <ModalLabel id="routing-rule-recipients-label">Recipients</ModalLabel>
                  <Select
                    aria-labelledby="routing-rule-recipients-label"
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="DMS users — inbox + notification"
                    value={modalForm.recipient_ids}
                    onChange={(recipient_ids) => setForm({ recipient_ids })}
                    options={userOptions}
                  />
                  <ModalLabel id="routing-rule-emails-label">Group Email Addresses</ModalLabel>
                  <Select
                    aria-labelledby="routing-rule-emails-label"
                    mode="tags"
                    allowClear
                    placeholder="e.g. SSD@wernerhoffman.com — gets an email with a link"
                    value={modalForm.notify_emails}
                    onChange={(notify_emails) => setForm({ notify_emails })}
                    open={false}
                  />
                </>
              )}

              <InlineRow>
                <Switch
                  size="small"
                  checked={modalForm.is_active}
                  onChange={(is_active) => setForm({ is_active })}
                  aria-label="Rule active"
                />
                <span>{modalForm.is_active ? "Rule is active" : "Rule is off"}</span>
              </InlineRow>

              {modal.error && <ModalError role="alert">{modal.error}</ModalError>}
              <ModalFooter>
                <Button type="button" onClick={closeModal} disabled={modal.saving}>
                  Cancel
                </Button>
                <Button type="submit" $primary disabled={modal.saving}>
                  {modal.saving
                    ? "Saving..."
                    : modal.mode === "edit"
                      ? "Update Rule"
                      : "Create Rule"}
                </Button>
              </ModalFooter>
            </ModalBody>
          </RuleModal>
        </Overlay>
      )}
    </Panel>
  );
}
